// Public Auto Lifestyle GPS — landing + Map My Route wizard.
// Presentation only: all routing intelligence comes from POST /api/auto-lifestyle/gps/journey.
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, Loader2 } from 'lucide-react';
import {
  DESTINATION_OPTIONS, PRIORITY_OPTIONS, trackGps, saveJourneyToken,
} from '../lib/gpsShared';

const API = process.env.REACT_APP_BACKEND_URL;

const SITUATIONS = [
  'I own vehicles and drive myself',
  'I use car services / get driven regularly',
  'A mix of both',
  'My situation is changing',
];

export default function AutoLifestyleGPS() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = landing
  const [situation, setSituation] = useState('');
  const [destType, setDestType] = useState('');
  const [destText, setDestText] = useState('');
  const [priorities, setPriorities] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const togglePriority = (p) =>
    setPriorities((prev) => prev.includes(p) ? prev.filter(x => x !== p)
      : prev.length >= 3 ? prev : [...prev, p]);

  const startMapping = () => { setStep(1); trackGps('gps_started'); };

  const calculate = async () => {
    setBusy(true); setError('');
    try {
      const type = destType || 'CUSTOM_DESTINATION';
      const text = destText.trim() ||
        (DESTINATION_OPTIONS.find(d => d.type === destType)?.label || '');
      const body = { destination_type: type, destination_text: text, priorities };
      if (situation) body.meta_situation = situation;
      const r = await fetch(`${API}/api/auto-lifestyle/gps/journey`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Could not map your route');
      const d = await r.json();
      saveJourneyToken(d.journey_id, d.access_token);
      trackGps('gps_route_created', d.journey_id);
      navigate(`/auto-lifestyle/route/${d.journey_id}?t=${d.access_token}`);
    } catch (e) { setError(String(e.message || e)); }
    setBusy(false);
  };

  const Pill = ({ active, children, onClick, testid }) => (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-3 border text-left text-sm tracking-wide transition-colors ${active
        ? 'border-red-700 bg-red-700/10 text-black font-semibold'
        : 'border-gray-300 text-gray-700 hover:border-black'}`}>
      {children}
    </button>
  );

  return (
    <div className="min-h-screen bg-white text-black flex flex-col">
            <main className="flex-1">
        {step === 0 && (
          <section className="max-w-5xl mx-auto px-5 py-16 sm:py-24" data-testid="gps-landing">
            <p className="text-xs uppercase tracking-[0.35em] text-red-700 mb-4">♠ Auto Lifestyle GPS</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] mb-6">
              WHAT'S YOUR<br />AUTO LIFESTYLE?
            </h1>
            <p className="text-base md:text-lg text-gray-700 max-w-xl mb-2">
              Where are you now? Where do you want to go?
            </p>
            <p className="text-base md:text-lg text-gray-700 max-w-xl mb-8">
              Are you <span className="font-semibold">Driven</span> or <span className="font-semibold">Driving</span>? ACE helps map the route.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-14">
              <button onClick={startMapping} data-testid="gps-map-my-route-btn"
                className="bg-black text-white px-8 py-4 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-red-700 transition-colors inline-flex items-center justify-center gap-2">
                Map My Route <ArrowRight className="h-4 w-4" />
              </button>
              <Link to="/booking" data-testid="gps-i-know-what-i-need"
                className="border border-black px-8 py-4 text-sm uppercase tracking-[0.2em] font-semibold hover:border-red-700 hover:text-red-700 transition-colors text-center">
                I Know What I Need
              </Link>
              <Link to="/contact" data-testid="gps-ask-concierge"
                className="border border-gray-300 px-8 py-4 text-sm uppercase tracking-[0.2em] text-gray-700 hover:border-black hover:text-black transition-colors text-center">
                Ask Your Auto Concierge
              </Link>
            </div>
            <div className="border-t border-gray-200 pt-8 grid sm:grid-cols-2 gap-6 max-w-3xl">
              <p className="text-sm text-gray-500 italic">Everyone drives. Not everyone is driven.</p>
              <p className="text-sm text-gray-500 italic">Your Auto Lifestyle can change. Your route can too.</p>
            </div>
          </section>
        )}

        {step > 0 && (
          <section className="max-w-2xl mx-auto px-5 py-12 sm:py-16" data-testid={`gps-step-${step}`}>
            <button onClick={() => setStep(step - 1)} className="text-xs uppercase tracking-widest text-gray-500 hover:text-black mb-6 inline-flex items-center gap-1" data-testid="gps-back-btn">
              <ChevronLeft className="h-3 w-3" /> Back
            </button>
            <div className="flex gap-1.5 mb-8">
              {[1, 2, 3, 4].map(n => (
                <div key={n} className={`h-1 flex-1 ${n <= step ? 'bg-red-700' : 'bg-gray-200'}`} />
              ))}
            </div>

            {step === 1 && (<>
              <h2 className="text-lg font-bold uppercase tracking-wide mb-2">What does your Auto Lifestyle look like today?</h2>
              <p className="text-sm text-gray-500 mb-6">Where are you now?</p>
              <div className="grid gap-3">
                {SITUATIONS.map(s => (
                  <Pill key={s} active={situation === s} onClick={() => setSituation(s)}
                    testid={`gps-situation-${SITUATIONS.indexOf(s)}`}>{s}</Pill>
                ))}
              </div>
              <button onClick={() => setStep(2)} data-testid="gps-step1-next"
                className="mt-8 bg-black text-white px-8 py-3 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-red-700 transition-colors">
                Continue
              </button>
            </>)}

            {step === 2 && (<>
              <h2 className="text-lg font-bold uppercase tracking-wide mb-2">Where do you want to go?</h2>
              <p className="text-sm text-gray-500 mb-6">Pick a destination or describe it in your own words.</p>
              <div className="grid sm:grid-cols-2 gap-2.5 mb-6">
                {DESTINATION_OPTIONS.map(d => (
                  <Pill key={d.type} active={destType === d.type}
                    onClick={() => { setDestType(d.type); trackGps('gps_destination_selected'); }}
                    testid={`gps-dest-${d.type}`}>{d.label}</Pill>
                ))}
              </div>
              <input value={destText} onChange={e => setDestText(e.target.value)}
                placeholder="Or tell us in your own words…" data-testid="gps-dest-text"
                className="w-full border border-gray-300 px-4 py-3 text-sm focus:border-black outline-none mb-6" />
              <button onClick={() => setStep(3)} disabled={!destType && !destText.trim()}
                data-testid="gps-step2-next"
                className="bg-black text-white px-8 py-3 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-red-700 transition-colors disabled:opacity-30">
                Continue
              </button>
            </>)}

            {step === 3 && (<>
              <h2 className="text-lg font-bold uppercase tracking-wide mb-2">What matters most on this route?</h2>
              <p className="text-sm text-gray-500 mb-6">Select up to three.</p>
              <div className="flex flex-wrap gap-2.5 mb-8">
                {PRIORITY_OPTIONS.map(p => (
                  <button key={p} onClick={() => togglePriority(p)} data-testid={`gps-priority-${p}`}
                    className={`px-4 py-2 border text-xs uppercase tracking-[0.15em] transition-colors ${priorities.includes(p)
                      ? 'border-red-700 bg-red-700 text-white'
                      : 'border-gray-300 text-gray-600 hover:border-black'}`}>
                    {p}
                  </button>
                ))}
              </div>
              <button onClick={() => setStep(4)} data-testid="gps-step3-next"
                className="bg-black text-white px-8 py-3 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-red-700 transition-colors">
                Continue
              </button>
            </>)}

            {step === 4 && (<>
              <h2 className="text-lg font-bold uppercase tracking-wide mb-6">Ready to map your route</h2>
              <div className="border border-gray-200 p-5 space-y-3 mb-8 text-sm">
                {situation && <p><span className="text-gray-500 uppercase text-xs tracking-widest">Today:</span> {situation}</p>}
                <p><span className="text-gray-500 uppercase text-xs tracking-widest">Destination:</span> {destText.trim() || DESTINATION_OPTIONS.find(d => d.type === destType)?.label}</p>
                {priorities.length > 0 && <p><span className="text-gray-500 uppercase text-xs tracking-widest">Priorities:</span> {priorities.join(' · ')}</p>}
              </div>
              {error && <p className="text-red-700 text-sm mb-4" data-testid="gps-error">{error}</p>}
              <button onClick={calculate} disabled={busy} data-testid="gps-calculate-btn"
                className="bg-red-700 text-white px-10 py-4 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-black transition-colors inline-flex items-center gap-2">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Calculate My Route <ArrowRight className="h-4 w-4" /></>}
              </button>
            </>)}
          </section>
        )}
      </main>
          </div>
  );
}
